import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import {
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list/types';
import { createDefaultHabitSchedule } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { TypeSections } from 'shared/types/page-sections';
import { Task } from 'shared/types/task';

import { useTypeTree } from '../../hooks/use-type-tree';
import { GroupedListProps } from '../../types';
import { TreeTaskCard } from '../tree-task-card';
import { TypeHeader } from '../type-header';
import * as Styled from './type-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const TypeGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTypeTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Type || item.children) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, []);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const typeId = item.sourceId.replace('-new-task', '');
          return (
            <NewTaskRow
              date={
                typeId === TypeSections.Tasks ? defaultNewTaskDate : undefined
              }
              schedules={
                typeId === TypeSections.Habits
                  ? [createDefaultHabitSchedule()]
                  : undefined
              }
              startTime={null}
              duration={null}
              goal={groupSettings.goalId}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Section Header (Type)
        if (type === TreeItemTypes.Type) {
          return <TypeHeader item={itemInstance} denseFormat={denseFormat} />;
        }

        return null;
      },
    [
      defaultNewTaskDate,
      denseFormat,
      groupSettings.goalId,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
      groupSettings.displayDue,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
